/**
 * Verifica se a loja está aberta com base nos dados da organização.
 * Considera pausa temporária (pausado_ate) e horário de funcionamento.
 *
 * @param {object} orgData - Dados da organização vindos do MenuContext.
 * @returns {object} { aberta: boolean, mensagem?: string, motivo?: 'pausa'|'fechado' }
 */
export function verificarLojaAberta(orgData) {
  if (!orgData) {
    return { aberta: true };
  }

  // Horário de funcionamento é interpretado no fuso de Brasília, igual ao backend.
  const agora = new Date();
  const timeZone = 'America/Sao_Paulo';

  const pausadoAte = orgData.pausado_ate ? new Date(orgData.pausado_ate) : null;
  if (pausadoAte && pausadoAte > agora) {
    const hora = pausadoAte.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
    return {
      aberta: false,
      mensagem: `Loja fechada temporariamente até ${hora}.`,
      motivo: 'pausa',
    };
  }

  let funcionamento = orgData.funcionamento;
  if (typeof funcionamento === 'string') {
    try {
      funcionamento = JSON.parse(funcionamento);
    } catch {
      return { aberta: false, mensagem: 'Horário de funcionamento inválido.', motivo: 'fechado' };
    }
  }

  const diaSemana = agora
    .toLocaleDateString('en-US', { weekday: 'long', timeZone })
    .toLowerCase();
  const dayManager = funcionamento?.dias?.[diaSemana] || funcionamento?.[diaSemana];

  if (!dayManager || dayManager.disabled !== true) {
    return { aberta: false, mensagem: 'Loja fechada no momento.', motivo: 'fechado' };
  }

  const toMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = String(timeStr).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const nowMinutes = agora.getHours() * 60 + agora.getMinutes();

  const start1 = toMinutes(dayManager.open);
  const end1 = toMinutes(dayManager.close);
  const start2 = toMinutes(dayManager.open2);
  const end2 = toMinutes(dayManager.close2);

  const dentroT1 = start1 !== null && end1 !== null && nowMinutes >= start1 && nowMinutes <= end1;
  const dentroT2 = start2 !== null && end2 !== null && nowMinutes >= start2 && nowMinutes <= end2;

  if (dentroT1 || dentroT2) {
    return { aberta: true };
  }

  const proximoHorario =
    start2 !== null && nowMinutes < start2 && nowMinutes > end1 ? dayManager.open2 : dayManager.open;

  return {
    aberta: false,
    mensagem: `Loja fechada no momento. Reabrimos às ${proximoHorario || dayManager.open}.`,
    motivo: 'fechado',
  };
}

// Helper interno: extrai o objeto de configuração de um dia específico do JSON de funcionamento.
function getDayManagerForDate(funcionamento, date) {
  const diaSemana = date
    .toLocaleDateString('en-US', { weekday: 'long' })
    .toLowerCase();
  return funcionamento?.dias?.[diaSemana] || funcionamento?.[diaSemana];
}

function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = String(timeStr).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Retorna o próximo horário em que a loja estará disponível para atender pedidos,
 * considerando abertura do dia e pausa temporária. Usado para calcular o mínimo
 * permitido de agendamento quando a loja está fechada no momento.
 *
 * Procura até 7 dias à frente — a loja pode ficar fechada por vários dias
 * seguidos (ex.: fechado às segundas e terças), não só "hoje já fechou,
 * abre amanhã".
 */
export function getProximoHorarioAbertura(orgData) {
  if (!orgData) return null;

  let funcionamento = orgData.funcionamento;
  if (typeof funcionamento === 'string') {
    try {
      funcionamento = JSON.parse(funcionamento);
    } catch {
      return null;
    }
  }

  const agora = new Date();
  const pausadoAte = orgData.pausado_ate ? new Date(orgData.pausado_ate) : null;

  for (let offset = 0; offset <= 7; offset++) {
    const dataCandidata = new Date(agora);
    dataCandidata.setDate(dataCandidata.getDate() + offset);
    const dayManager = getDayManagerForDate(funcionamento, dataCandidata);
    if (!dayManager || dayManager.disabled !== true) continue;

    const start1 = timeToMinutes(dayManager.open);
    const start2 = timeToMinutes(dayManager.open2);

    let proximoMinutos;
    if (offset === 0) {
      const nowMinutes = agora.getHours() * 60 + agora.getMinutes();
      if (start1 !== null && nowMinutes < start1) {
        proximoMinutos = start1;
      } else if (start2 !== null && nowMinutes < start2) {
        proximoMinutos = start2;
      } else {
        continue; // hoje já fechou os dois turnos, tenta o próximo dia
      }
    } else {
      proximoMinutos = start1 !== null ? start1 : start2;
    }

    if (proximoMinutos == null) continue;

    const proximo = new Date(dataCandidata);
    proximo.setSeconds(0, 0);
    proximo.setHours(Math.floor(proximoMinutos / 60), proximoMinutos % 60);

    if (pausadoAte && pausadoAte > agora && pausadoAte > proximo) {
      return pausadoAte;
    }
    return proximo;
  }

  return null;
}

/**
 * Retorna o horário mínimo permitido para um agendamento.
 * Se a loja está aberta agora, usa agora + tempo_antecedencia_agendamento.
 * Se está fechada, usa o próximo horário de abertura + tempo_antecedencia_agendamento.
 */
export function getMinimoAgendamento(orgData) {
  if (!orgData) return null;

  const status = verificarLojaAberta(orgData);
  const antecedencia = Number(orgData.tempo_antecedencia_agendamento) || 60;

  if (status.aberta) {
    return new Date(Date.now() + antecedencia * 60000);
  }

  const abertura = getProximoHorarioAbertura(orgData);
  if (!abertura) return null;

  return new Date(abertura.getTime() + antecedencia * 60000);
}

/**
 * Retorna o horário máximo permitido para um agendamento, baseado em
 * orgData.dias_max_agendamento. Se o campo estiver vazio/0, retorna null
 * (sem limite).
 */
export function getMaximoAgendamento(orgData) {
  const dias = Number(orgData?.dias_max_agendamento);
  if (!dias || dias <= 0) return null;

  const maximo = new Date();
  maximo.setDate(maximo.getDate() + dias);
  return maximo;
}

/**
 * Formata a data/hora de reabertura (retorno de getProximoHorarioAbertura) de
 * forma legível: "hoje às HH:mm", "amanhã às HH:mm" ou "dd/mm às HH:mm" para
 * datas mais distantes. Evita mensagens vagas tipo "mais tarde" quando a loja
 * fica fechada por vários dias seguidos.
 */
export function formatarProximaAbertura(data) {
  if (!data) return null;

  const agora = new Date();
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const diaAbertura = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  const diffDias = Math.round((diaAbertura - hoje) / 86400000);
  const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (diffDias === 0) return `hoje às ${hora}`;
  if (diffDias === 1) return `amanhã às ${hora}`;
  const dataFormatada = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${dataFormatada} às ${hora}`;
}
